import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { resolveClientSession } from '@/lib/auth-session';
import { socketService } from '@/services/socket.service';
import { api } from '@/services/api.service';
import { useAuthStore } from './auth.store';
import { queryClient } from '@/lib/query-client';
import { useServersStore } from '@/features/servers/servers.store';
import { useChannelsStore } from '@/features/channels/channels.store';
import { useChatStore } from '@/features/chat/chat.store';
import type { UserProfile } from '@disenchantment/types';
import { AUTH_BRIDGE_PATH } from '@disenchantment/auth-client';
import { AUTH_CALLBACK_PATH, AUTH_ERROR_PATH } from '@/lib/auth-redirect';
import { clearAllSupabaseAuthStorage } from '@/lib/auth-persistence';

function isPublicGuestPath(pathname: string): boolean {
  if (
    pathname === AUTH_CALLBACK_PATH ||
    pathname.startsWith(`${AUTH_CALLBACK_PATH}/`) ||
    pathname === AUTH_BRIDGE_PATH ||
    pathname.startsWith(`${AUTH_BRIDGE_PATH}/`) ||
    pathname === AUTH_ERROR_PATH ||
    pathname.startsWith(`${AUTH_ERROR_PATH}/`)
  ) {
    return true;
  }
  if (pathname === '/login' || pathname === '/register') return true;
  if (pathname.startsWith('/invite/')) return true;
  return false;
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Subscribes to Supabase auth state changes, syncs session to the store,
 * and connects/disconnects the websocket gateway on auth transitions.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { setSession, setUser, setLoading, clear } = useAuthStore();
  const clearServers = useServersStore((s) => s.clear);
  const clearChannels = useChannelsStore((s) => s.clear);
  const clearChat = useChatStore((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();
  const hydratedRef = useRef(false);
  const profileLoadRef = useRef<Promise<void> | null>(null);
  const pathnameRef = useRef(location.pathname);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const getSessionSnapshot = (session: Session | null) =>
      session
        ? {
            userId: session.user.id,
            expiresAt: session.expires_at ?? null,
          }
        : null;

    const logAuthState = (redirectReason: string | null, session: Session | null) => {
      console.log('[AuthProvider] state', {
        pathname: pathnameRef.current,
        loading: useAuthStore.getState().loading,
        session: getSessionSnapshot(session),
        redirectReason,
      });
    };

    const loadProfile = () => {
      if (profileLoadRef.current) return profileLoadRef.current;
      profileLoadRef.current = api
        .get<UserProfile>('/api/users/me')
        .then(setUser)
        .catch((err) => {
          console.warn('[AuthProvider] failed to load profile', err);
        })
        .finally(() => {
          profileLoadRef.current = null;
        });
      return profileLoadRef.current;
    };

    const applySession = (session: Session | null, reason: string) => {
      if (!isMounted) return;
      setSession(session);
      hydratedRef.current = true;
      setLoading(false);
      logAuthState(reason, session);

      if (session?.access_token) {
        localStorage.setItem('sb-access-token', session.access_token);
        socketService.connect(session.access_token);
        void loadProfile();
      } else {
        localStorage.removeItem('sb-access-token');
      }
    };

    const hydrateOnce = async (reason: string) => {
      if (hydratedRef.current) return;
      const session = await resolveClientSession();
      applySession(session, reason);
    };

    void hydrateOnce('initial-session-hydrated');

    // Listen for ongoing auth changes (sign in, sign out, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      logAuthState(session ? null : `auth-event:${event}`, session);

      if (!hydratedRef.current && event === 'INITIAL_SESSION') {
        void hydrateOnce('initial-session-from-listener');
        return;
      }

      setSession(session);
      setLoading(false);

      if (session?.access_token) {
        localStorage.setItem('sb-access-token', session.access_token);
        if (event === 'SIGNED_IN') {
          // Re-login: reuse an open socket when possible (avoids connect storms after password change).
          if (socketService.isConnected || socketService.isConnecting) {
            socketService.updateToken(session.access_token);
          } else {
            socketService.connect(session.access_token);
          }
        } else if (event === 'TOKEN_REFRESHED') {
          // Token rotated — update auth on the existing socket so the next
          // reconnect attempt uses the new token. Do NOT call connect() here:
          // the socket is already open and connect() is now guarded by instance
          // existence, meaning a second call would be silently ignored anyway.
          socketService.updateToken(session.access_token);
        } else {
          // Any other event (USER_UPDATED, etc.) — connect if not already done.
          socketService.connect(session.access_token);
        }
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          void loadProfile();
        }
      } else {
        // Session removed (signed out). Clear client caches and redirect.
        clearAllSupabaseAuthStorage();
        socketService.disconnect();
        clear();
        clearServers();
        clearChannels();
        clearChat();
        try {
          // Clear react-query cache to avoid leaking authenticated data.
          queryClient.clear();
        } catch (e) {
          // ignore
        }

        if (!isPublicGuestPath(pathnameRef.current)) {
          console.log('[AuthProvider] redirecting to login', {
            pathname: pathnameRef.current,
            loading: useAuthStore.getState().loading,
            session: getSessionSnapshot(session),
            redirectReason: `auth-event:${event}`,
          });
          navigate('/login', {
            replace: true,
            state: { reason: `auth-event:${event}` },
          });
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [clear, clearChannels, clearChat, clearServers, navigate, setLoading, setSession, setUser]);

  return <>{children}</>;
}
